import React, { useContext } from "react";

// Import Contexts
import { LanguageContext, ScreenContext, SessionContext } from "contexts";

// Import Components
import {
  TitleBar,
  SecondaryButton,
  NormalText,
  PickProcedure,
} from "components/index";
import { Language, ProcedureType, TScreen } from "api/type";
import { getTranslation } from "utils/translate";
import { isFaceProcedure } from "utils/isFaceBodyOrTorsoProcedure";
import {
  postNotifyUnsupportedProcedure,
  TPostNotifyUnsupportedProcedureRequest,
} from "api/clinicos";
import { TUrlParamsInfoOnboardingScan } from "./RootOnboardingScan";
import { TImageBlobAndViewType } from "components/scan/Scan";

type ScreenVisualizeNonAISupportProps = {
  urlParamsInfo: TUrlParamsInfoOnboardingScan | null;
  selectedProcedure: ProcedureType | null;
  handleSaveSelectedProcedure: (selectedProcedure: ProcedureType) => void;
  handleSaveBlobAndPresignedUrl: (
    blobAndPresignedUrl: TImageBlobAndViewType | null,
  ) => void;
};

const allowedProcedures = [
  ProcedureType.BREAST_AUGMENTATION,
  ProcedureType.BREAST_REDUCTION,
  ProcedureType.BREAST_LIFT,
  ProcedureType.BREAST_LIFT_AND_AUGMENTATION,
  ProcedureType.TUMMY_TUCK,
  ProcedureType.BBL,
];

const ScreenVisualizeNonAISupport: React.FC<
  ScreenVisualizeNonAISupportProps
> = ({
  urlParamsInfo,
  selectedProcedure,
  handleSaveSelectedProcedure,
  handleSaveBlobAndPresignedUrl,
}) => {
  // Context
  const language = useContext<Language>(LanguageContext)[0];
  const session = useContext(SessionContext);
  const setScreen = useContext(ScreenContext)[1];

  React.useEffect(() => {
    if (!session || !selectedProcedure || !urlParamsInfo) return;
    const callNotifyUnsupoortedProcedure = async () => {
      if (!session || !selectedProcedure) return;
      try {
        const body: TPostNotifyUnsupportedProcedureRequest = {
          name: urlParamsInfo.name || "Not captured",
          email: urlParamsInfo.email || "Not captured",
          procedure_type: selectedProcedure,
        };

        await postNotifyUnsupportedProcedure(session, body);
      } catch (error) {
        console.error("Error in postNotifyUnsupportedProcedure", error);
      }
    };
    callNotifyUnsupoortedProcedure();
  }, [session, selectedProcedure, urlParamsInfo]);

  const [selectedProcedure2, setSelectedProcedure2] =
    React.useState<ProcedureType | null>(null);

  const handleOnProcedureSelect = (selectedProcedure: ProcedureType) => {
    setSelectedProcedure2(selectedProcedure);
    handleSaveSelectedProcedure(selectedProcedure);
    if (selectedProcedure === ProcedureType.BBL) {
      setScreen(TScreen.BODY_SCAN_SCREEN);
      handleSaveBlobAndPresignedUrl(null);
    } else {
      setScreen(TScreen.SIMULATION_VIEW_SCREEN);
    }
  };

  if (!selectedProcedure) return null;

  return (
    <div className="screen-container">
      <TitleBar
        name={getTranslation("visualizationCommingSoon", language)}
        onClickBackButton={null}
        centerTitle
        showLogo={false}
      />
      {isFaceProcedure(selectedProcedure) ? (
        <div style={{ margin: "auto", padding: "20px" }}>
          <NormalText
            textContent={getTranslation("instantVisualization", language)}
            style={{ textAlign: "center", marginBottom: "10px" }}
          />
          <NormalText
            textContent={getTranslation(
              "wellEmailYourVisualizationToYouInTheNext12Hours",
              language,
            )}
            style={{ textAlign: "center" }}
          />
        </div>
      ) : (
        <>
          <div style={{ padding: "0px 20px" }}>
            <NormalText
              textContent={getTranslation(
                "instantVisualizationAndMore",
                language,
              )}
              style={{ textAlign: "center", marginBottom: "20px" }}
            />
            <NormalText
              textContent={getTranslation(
                "tryAProcedureWithInstantVisualization",
                language,
              )}
              style={{ textAlign: "center" }}
            />
          </div>

          <PickProcedure
            initialProcedure={selectedProcedure2}
            allowedProcedures={allowedProcedures}
            aiProcedures={[]}
            gap={10}
            onProcedureSelect={(selectedProcedure) => {
              handleOnProcedureSelect(selectedProcedure);
            }}
          />
        </>
      )}
      <SecondaryButton
        onClick={() => {
          window.parent.postMessage({ message: "close modal" }, "*");
        }}
        fullWidth
        text={getTranslation("continueToMyQuote", language)}
      />
    </div>
  );
};

export default ScreenVisualizeNonAISupport;
